import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import type { Athlete, AthleteAssignment } from "@/lib/types";
import {
  useCreateAssignment,
  useEndAssignment,
  useTransferAssignment,
} from "../hooks/useAthletes";
import {
  assignmentChipLabel,
  FIELD_CLASS,
  FIELD_LABEL_CLASS,
  REJOIN_LABEL_SUFFIX,
  rosterTargets,
  teamChoiceLabel,
  type RosterTarget,
  type TeamChoice,
} from "../athleteDisplay";

/**
 * The three roster-assignment actions of the athlete detail (#908, plan
 * §6): roster an existing athlete on a team, transfer an assignment
 * (= end here + create on the destination, one transaction) and end one.
 * Ending an assignment drops that team's projected memberships, so every
 * confirmation that ends one carries the §5.4 sentence verbatim (D12).
 */

/** §5.4 / D12 — the sentence every removal confirmation must carry. */
export const END_ASSIGNMENT_COPY =
  "This removes the athlete's access to this season's team content.";

function teamOptions(targets: RosterTarget<TeamChoice>[]) {
  return [
    { value: "", label: "Pick a team", disabled: true },
    ...targets.map(({ team, rejoin }) => ({
      value: team.id,
      label: `${teamChoiceLabel(team)}${rejoin ? REJOIN_LABEL_SUFFIX : ""}`,
    })),
  ];
}

export function RosterAssignmentDialog({
  athlete,
  teams,
  teamsLoading,
  assignments,
  onClose,
  onSaved,
}: {
  /** Only the id and name are read (the directory row passes its entry). */
  athlete: Pick<Athlete, "id" | "display_name">;
  teams: TeamChoice[];
  teamsLoading: boolean;
  /** The athlete's assignments: a team with an ACTIVE one is left out
   * (409), a team with only an ENDED one is offered as a rejoin (the
   * backend reinstates that row). */
  assignments: readonly AthleteAssignment[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const options = useMemo(
    () => teamOptions(rosterTargets(teams, assignments)),
    [teams, assignments],
  );
  const [teamId, setTeamId] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useCreateAssignment();

  const submit = async () => {
    setError(null);
    if (!teamId) {
      setError("Pick a team.");
      return;
    }
    try {
      await mut.mutateAsync({
        athlete_id: athlete.id,
        team_id: teamId,
        jersey_number: jersey.trim(),
        position: position.trim(),
        starts_on: startsOn || null,
      });
      toast.success(`${athlete.display_name} rostered`);
      onSaved();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't roster the athlete", { description: message });
    }
  };

  return (
    <ConfirmDialog
      title="Roster on a team"
      confirmDisabled={mut.isPending || teamsLoading}
      message={
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink-muted">
            Adds one assignment of{" "}
            <span className="font-display text-ink">{athlete.display_name}</span>{" "}
            to the team. Linked accounts get that team&rsquo;s membership.
          </p>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>
              Team <strong aria-hidden>*</strong>
            </span>
            <TkticSelect
              value={teamId}
              onChange={setTeamId}
              options={options}
              disabled={teamsLoading}
              placeholder={teamsLoading ? "Loading teams…" : "Pick a team"}
              size="sm"
              ariaLabel="Team to roster on"
              data-testid="admin-athlete-roster-team"
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Jersey</span>
              <input
                type="text"
                value={jersey}
                onChange={(e) => setJersey(e.target.value)}
                className={FIELD_CLASS}
                data-testid="admin-athlete-roster-jersey"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Position</span>
              <input
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className={FIELD_CLASS}
                data-testid="admin-athlete-roster-position"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>Start date</span>
            <TkticDateInput
              value={startsOn}
              onChange={setStartsOn}
              data-testid="admin-athlete-roster-starts"
            />
          </label>
          {error ? (
            <p
              data-testid="admin-athlete-roster-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Rostering…" : "Roster athlete"}
      onConfirm={() => void submit()}
      onClose={onClose}
      data-testid="admin-athlete-roster-dialog"
    />
  );
}

export function TransferAssignmentDialog({
  athleteName,
  assignment,
  assignments,
  teams,
  teamsLoading,
  onClose,
  onSaved,
}: {
  athleteName: string;
  assignment: AthleteAssignment;
  /** Every assignment of the athlete: teams with an ACTIVE one (the source
   * included) are left out, ENDED-only teams are offered as a rejoin. */
  assignments: readonly AthleteAssignment[];
  teams: TeamChoice[];
  teamsLoading: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const options = useMemo(
    () =>
      teamOptions(
        rosterTargets(teams, assignments).filter(
          ({ team }) => team.id !== assignment.team_id,
        ),
      ),
    [teams, assignments, assignment.team_id],
  );
  const [toTeamId, setToTeamId] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useTransferAssignment();

  const submit = async () => {
    setError(null);
    if (!toTeamId) {
      setError("Pick the destination team.");
      return;
    }
    try {
      await mut.mutateAsync({
        playerId: assignment.player_id,
        body: {
          to_team_id: toTeamId,
          ends_on: endsOn || null,
          starts_on: startsOn || null,
        },
      });
      toast.success(`${athleteName} transferred`);
      onSaved();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't transfer the assignment", { description: message });
    }
  };

  return (
    <ConfirmDialog
      title="Transfer assignment"
      confirmDisabled={mut.isPending || teamsLoading}
      message={
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink">
            Ends <span className="font-display">{athleteName}</span>&rsquo;s
            assignment on{" "}
            <span className="font-display">{assignmentChipLabel(assignment)}</span>{" "}
            and rosters them on the destination team in one step.
          </p>
          <p className="text-xs text-ink-muted">{END_ASSIGNMENT_COPY}</p>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>
              Destination team <strong aria-hidden>*</strong>
            </span>
            <TkticSelect
              value={toTeamId}
              onChange={setToTeamId}
              options={options}
              disabled={teamsLoading}
              placeholder={teamsLoading ? "Loading teams…" : "Pick a team"}
              size="sm"
              ariaLabel="Destination team"
              data-testid="admin-athlete-transfer-team"
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Ends on</span>
              <TkticDateInput
                value={endsOn}
                onChange={setEndsOn}
                data-testid="admin-athlete-transfer-ends"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Starts on</span>
              <TkticDateInput
                value={startsOn}
                onChange={setStartsOn}
                data-testid="admin-athlete-transfer-starts"
              />
            </label>
          </div>
          {error ? (
            <p
              data-testid="admin-athlete-transfer-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Transferring…" : "Transfer"}
      onConfirm={() => void submit()}
      onClose={onClose}
      data-testid="admin-athlete-transfer-dialog"
    />
  );
}

export function EndAssignmentDialog({
  athleteName,
  assignment,
  onClose,
  onEnded,
}: {
  athleteName: string;
  assignment: AthleteAssignment;
  onClose: () => void;
  onEnded: () => void;
}) {
  const toast = useToast();
  const [endsOn, setEndsOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mut = useEndAssignment();

  const submit = async () => {
    setError(null);
    try {
      await mut.mutateAsync({
        playerId: assignment.player_id,
        body: { ends_on: endsOn || null },
      });
      toast.success("Assignment ended");
      onEnded();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't end the assignment", { description: message });
    }
  };

  return (
    <ConfirmDialog
      title="End assignment"
      danger
      confirmDisabled={mut.isPending}
      message={
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink">
            End <span className="font-display">{athleteName}</span>&rsquo;s
            assignment on{" "}
            <span className="font-display">{assignmentChipLabel(assignment)}</span>?
          </p>
          <p data-testid="admin-athlete-end-copy" className="text-xs text-ink-muted">
            {END_ASSIGNMENT_COPY}
          </p>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>Ends on (optional)</span>
            <TkticDateInput
              value={endsOn}
              onChange={setEndsOn}
              data-testid="admin-athlete-end-date"
            />
          </label>
          {error ? (
            <p
              data-testid="admin-athlete-end-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Ending…" : "End assignment"}
      onConfirm={() => void submit()}
      onClose={onClose}
      data-testid="admin-athlete-end-dialog"
    />
  );
}
